import UserAPI from "@api/User.api";
import SSPagination from "@core-components/pagination/Pagination";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Stack, Table } from "@mantine/core";
import { type ReactNode, useEffect, useState } from "react";
import type IReferral from "../types/IReferral";
import CoinsGainedBadge from "./CoinsGainedBadge";
import ContainerCard from "./container/ContainerCard";

const FIRST_PAGE_INDEX = 1;

const AffiliationTable = () => {
  const [cashbacks, setCashbacks] = useState<IReferral[]>([]);
  const [pagination, setPagination] = useState({
    current: FIRST_PAGE_INDEX,
    total: 0,
    itemsPerPage: 0,
    pages: 0,
  });

  useEffect(() => {
    getCashbacksFromReferrals(FIRST_PAGE_INDEX);
  }, []);

  const getCashbacksFromReferrals = (page: number) => {
    UserAPI.getCashbacksFromReferrals(page).then((res) => {
      setCashbacks(res.data);
      setPagination({
        current: res.meta.currentPage,
        itemsPerPage: res.meta.perPage,
        total: res.meta.total,
        pages: Math.ceil(res.meta.total / res.meta.perPage),
      });
    });
  };

  const onPaginate = (current: number) => {
    setPagination((old) => ({ ...old, current }));
    getCashbacksFromReferrals(current);
  };

  const { t } = useAppTranslation("pages.clientDashboard.components");

  const rows = cashbacks.map((cashback, index) => (
    <Table.Tr
      key={`${cashback.email}-${cashback.date}-${cashback.coins}-${index}`}
      className="border-b-0 font-inter font-semibold"
    >
      <Table.Td className="text-center">{cashback.email}</Table.Td>
      <Table.Td className="text-center">
        {cashback.date.format("DD/MM/YYYY")}
      </Table.Td>
      <Table.Td className="text-center">
        <CoinsGainedBadge coins={cashback.coins} />
      </Table.Td>
    </Table.Tr>
  ));

  const Th = ({
    children,
    className,
  }: { children: ReactNode; className?: string }) => {
    return (
      <Table.Th
        className={`border-primary-light border-b-[1px] border-solid text-center font-normal text-sm ${className}`}
      >
        {children}
      </Table.Th>
    );
  };

  const noContent = (
    <Flex
      justify="center"
      align="center"
      className="sticky left-0 h-[150px] bg-primary-client text-center text-white"
    >
      {t("affiliationTable.noContent")}
    </Flex>
  );

  return (
    <ContainerCard
      title={t("affiliationTable.title")}
      classNames={{
        root: "flex-1",
        wrapper: "!p-3",
      }}
    >
      <Stack>
        <div className="overflow-auto">
          <Table className="border-separate rounded-md border-[1px] border-primary-light border-solid bg-primary-client text-primary-white">
            <Table.Thead className="h-14 bg-primary-background text-primary-dark-text">
              <Table.Tr>
                <Th className="rounded-tl-rounded-md">
                  {t("affiliationTable.headers.referralEmail")}
                </Th>
                <Th>{t("affiliationTable.headers.purchaseDate")}</Th>
                <Th className="rounded-tr-rounded-md">
                  {t("affiliationTable.headers.coinsEarned")}
                </Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
            {!rows.length && <Table.Td colSpan={3}>{noContent}</Table.Td>}
          </Table>
        </div>

        <SSPagination
          currentPage={pagination.current}
          itemsCount={pagination.total}
          itemsPerPage={pagination.itemsPerPage}
          pageCount={pagination.pages}
          onPaginate={onPaginate}
        />
      </Stack>
    </ContainerCard>
  );
};

export default AffiliationTable;
